import apiClient from "./api";

export interface GuardrailsPolicy {
  systemPolicy: string;
  companyPolicy: string;
  policyVersion: number;
  violationResponseText: string;
}

export interface UpdateCompanyPolicyDto {
  companyPolicyText: string;
}

export interface GuardrailsTestResult {
  allowed: boolean;
  violations: string[];
  reason: string;
}

/**
 * A prior conversation turn supplied to a guardrails test run, so the test reproduces the
 * widget's behavior (which feeds recent session messages as reference context).
 */
export interface GuardrailsTestTurn {
  role: "user" | "assistant";
  content: string;
}

export interface PolicyViolation {
  id: string;
  companyId: string;
  timestamp: string;
  incomingMessage: string;
  violationReasons: string[];
  blockingResponse: string;
  policyVersion: number;
}

export interface PolicyViolationDetail {
  id: string;
  companyId: string;
  timestamp: string;
  incomingMessage: string;
  violationReasons: string[];
  blockingResponse: string;
  policyVersion: number;
}

/** What a saved change did to the company policy. `Deleted` = the policy was cleared. */
export type PolicyHistoryAction = "Created" | "Updated" | "Deleted";

/**
 * One saved change to the company policy (`GET /guardrails/policy/history`).
 * For `Deleted`, `companyPolicyText` / `policyVersion` describe the version that was cleared.
 */
export interface PolicyHistoryEntry {
  id: string;
  action: PolicyHistoryAction;
  policyVersion: number;
  companyPolicyText: string;
  /** Text this save replaced; null for `Created` and `Deleted`. */
  previousPolicyText: string | null;
  /** Null for automated writes (no signed-in user). */
  changedByUserId: string | null;
  changedByEmail: string | null;
  changedAt: string;
}

export interface PaginatedResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export const guardrailsService = {
  getPolicy: async (): Promise<GuardrailsPolicy> => {
    const response = await apiClient.get("/guardrails/policy");
    return response.data;
  },

  updateCompanyPolicy: async (data: UpdateCompanyPolicyDto): Promise<void> => {
    await apiClient.put("/guardrails/policy", data);
  },

  /**
   * Regenerate the company policy from Company.Website + active toolkits via AI.
   * Requires Company.Website to be set and at least one active server, or returns 400.
   * Returns 422 if the generated text fails safety validation — existing policy stays untouched.
   */
  regenerate: async (): Promise<GuardrailsPolicy> => {
    const response = await apiClient.post("/guardrails/regenerate");
    return response.data;
  },

  testMessage: async (
    message: string,
    recentMessages?: GuardrailsTestTurn[],
  ): Promise<GuardrailsTestResult> => {
    const response = await apiClient.post("/guardrails/test", {
      message,
      recentMessages,
    });
    return response.data;
  },

  updateViolationResponse: async (
    violationResponseText: string | null,
  ): Promise<void> => {
    await apiClient.put("/guardrails/violation-response", {
      violationResponseText,
    });
  },

  getViolations: async (
    page: number = 1,
    pageSize: number = 20,
  ): Promise<PaginatedResult<PolicyViolation>> => {
    const response = await apiClient.get(`/guardrails/violations`, {
      params: { page, pageSize },
    });
    return response.data;
  },

  getViolation: async (id: string): Promise<PolicyViolationDetail> => {
    const response = await apiClient.get(`/guardrails/violations/${id}`);
    return response.data;
  },

  /**
   * Saved versions of the company policy, newest first, with who saved each.
   * Silent: the history card shows its own inline error state instead of a toast.
   */
  getPolicyHistory: async (
    limit: number = 50,
  ): Promise<PolicyHistoryEntry[]> => {
    const response = await apiClient.get("/guardrails/policy/history", {
      params: { limit },
      __silent: true,
    });
    return response.data;
  },
};
